/**
 * Честная пустота ленты: ветви по причине, не по двум stale-именам.
 *
 * 31.08.2026 кадр B: HOT=0 при 18 живых, баннер «молчат duelbits, polymarket».
 * Duelbits у сторожа был ✓, Poly age 11 с. Хозяин — упавший такт, не две площадки.
 */

import type { SourceInfo } from './meta';

export type EmptyKind =
  | 'paused'
  | 'reconnect'
  | 'loading'
  | 'tick_broken'
  | 'all_silent'
  | 'majority_silent'
  | 'required_venue'
  | 'entry_only'
  | 'filter'
  | 'exclude'
  | 'engine'
  | 'market'
  | 'away'
  | 'revoked';

export interface EmptyInput {
  status: string;
  framed: boolean;
  proto: number;
  sources: readonly SourceInfo[];
  tickBroken?: boolean | null;
  tickFailures?: number | null;
  lastFailureAgeSeconds?: number | null;
  hidden: number;
  filtering: boolean;
  byProfile: number;
  byHour: readonly number[];
  hour?: number;
  /** Профиль «мои конторы». Пустой рынок при живом HOT — ложь. */
  mine?: boolean;
  /**
   * Режим «главная»: конторы, хотя бы одна из которых обязана стоять на ноге.
   * Не «мои»: все отмеченные в профиле обязательными считать нельзя — так 31.08
   * пустой экран обозвал Полик, хотя он просто стоял в списке счетов.
   */
  mainVenues?: readonly string[];
  /** Требование ко входу: both + match/coupon = «только вход». */
  linkNeed?: 'no' | 'either' | 'both';
  prec?: 'any' | 'coupon' | 'match' | 'site';
  /** Сколько снял бан матча/исхода у кассы. */
  byExclude?: number;
  /** Сколько снял фильтр «свежесть от». */
  byFresh?: number;
  /** Тумблер «только вход» сита: прячет вилки без двери события/исхода. */
  entryOnly?: boolean;
  /** «исключён исход у Леона» — чтобы пустой экран не врал про рынок. */
  excludeSaid?: string | null;
  /** Страница лайв или прематч: чужая линия — не «фильтр скрыл». */
  pageLine?: 'live' | 'prematch' | null;
  /** Сколько вилок этой страницы в кадре, до сита. */
  here?: number;
  /** Сколько вилок соседней линии в кадре. */
  away?: number;
}

export interface EmptyView {
  kind: EmptyKind;
  silent: string[];
  said?: string;
}

/** Источник без новой цены, как сторож «новой цены нет», не age confirm. */
export function priceGone(source: SourceInfo): boolean {
  const limit = source.staleAfterSeconds;
  const origin = source.originAgeSeconds;
  if (origin !== null && origin !== undefined && limit !== null && origin > limit) {
    return true;
  }
  if (!source.stale) return false;
  // Старый кадр без origin: confirm-age свежий при stale — часы confirm, не молчание.
  const age = source.ageSeconds;
  if (age !== null && limit !== null && age <= limit) return false;
  return true;
}

export function silentCodes(sources: readonly SourceInfo[]): string[] {
  return sources.filter(priceGone).map((source) => source.code);
}

/**
 * Источник без единой цены — дырка в каталоге, не «молчит».
 *
 * Русская плоскость всегда регистрирует ch_v, хотя Вилка у экспортёра выключена
 * навсегда. quotes=0 + stale + age пуст — его никогда не было в эфире. Считать
 * его половиной «1 из 2» — врать, что лента пуста из‑за Вилки.
 */
export function sourceInService(source: SourceInfo): boolean {
  if (
    source.quotes === 0 &&
    source.stale &&
    (source.ageSeconds === null || source.ageSeconds === undefined)
  ) {
    return false;
  }
  return true;
}

/** Плотность часа врёт «спокойно», если последние шесть часов нули. */
export function densityHonest(byHour: readonly number[], hour: number): boolean {
  if (byHour.length !== 24) return false;
  if ((byHour[hour] ?? 0) <= 0) return false;
  for (let step = 0; step < 6; step += 1) {
    if ((byHour[(hour - step + 24) % 24] ?? 0) > 0) return true;
  }
  return false;
}

export function emptyView(input: EmptyInput): EmptyView {
  const sources = input.sources.filter(sourceInService);
  const silent = silentCodes(sources);
  if (input.status === 'revoked') return { kind: 'revoked', silent };
  if (input.status === 'paused') return { kind: 'paused', silent };
  // Обрыв без кадра — пустой экран. Живой кадр (сокет, REST, кэш) не стираем:
  // текст «цены, которые остались на экране» иначе врёт, а F5 во время такта
  // 5–15 с оставляет Hunt 0. Шапка уже пишет «связь потеряна».
  if (input.status === 'reconnecting' && !input.framed) return { kind: 'reconnect', silent };
  // Кадр решает, не HTTP-proto: снапшот уже привёз proto, а /v1/meta может
  // опаздывать или молчать. Кэш прошлого кадра тоже framed — это не загрузка.
  if (!input.framed) {
    return { kind: 'loading', silent };
  }

  const failedAgo = input.lastFailureAgeSeconds;
  const tickLive =
    input.tickBroken === true ||
    (typeof input.tickFailures === 'number' &&
      input.tickFailures > 0 &&
      failedAgo !== null &&
      failedAgo !== undefined &&
      failedAgo < 300);
  if (tickLive) return { kind: 'tick_broken', silent };

  if (sources.length > 0 && silent.length === sources.length) {
    return { kind: 'all_silent', silent };
  }
  if ((input.here ?? 0) === 0 && (input.away ?? 0) > 0 && input.pageLine) {
    return { kind: 'away', silent };
  }

  // Вилки есть, но их спрятали фильтры — причина пустоты фильтры, а не молчание
  // части источников. 26.09 русская лента (ch_v у экспортёра выключен навсегда)
  // говорила «молчит источник 1 из 2», пока «только вход» прятал 87 строк.
  if ((input.filtering || input.byProfile > 0 || (input.byExclude ?? 0) > 0) && input.hidden > 0) {
    if ((input.byFresh ?? 0) > 0 && input.byFresh === input.hidden) {
      return { kind: 'exclude', silent, said: `свежесть скрыла ${input.byFresh}` };
    }
    if ((input.byExclude ?? 0) > 0 && !input.filtering && input.byProfile === 0) {
      return {
        kind: 'exclude',
        silent,
        said: input.excludeSaid ?? 'исключён матч у конторы',
      };
    }
    if (input.mainVenues && input.mainVenues.length > 0) {
      return { kind: 'required_venue', silent: [...input.mainVenues] };
    }
    if (
      input.entryOnly === true ||
      (input.linkNeed === 'both' && (input.prec === 'match' || input.prec === 'coupon'))
    ) {
      return { kind: 'entry_only', silent };
    }
    return { kind: 'filter', silent };
  }

  // Часть имён — только если молчит большинство. Двое из двадцати при нуле HOT
  // не имеют права стать хозяином пустоты (кадр 18:12).
  if (silent.length > 0 && silent.length * 2 >= sources.length) {
    return { kind: 'majority_silent', silent };
  }



  const hour =
    input.hour ??
    (typeof Intl === 'object' ? new Date().getUTCHours() : 0);
  if (densityHonest(input.byHour, hour) && !tickLive && silent.length === 0) {
    return { kind: 'market', silent };
  }
  return { kind: 'engine', silent };
}
